import axiosInstance from "./axiosConfig";

export const alertApi = {
    /**
     * Get alerts (paginated, with optional vehicle search and date range)
     * @param {string|null} date - Date in YYYY-MM-DD format (optional)
     * @param {number} page - Page number (default 1)
     * @param {number} perPage - Items per page (default 20)
     * @param {string|null} vehiclePlate - Vehicle plate search filter
     * @param {string|null} dateFrom - Range start date in YYYY-MM-DD format (optional)
     * @param {string|null} dateTo - Range end date in YYYY-MM-DD format (optional)
     * @param {boolean} allOccurrences - When true, returns every confirmed occurrence
     *   per door in the date range (full history) instead of just the latest one
     *   per door (current status). Defaults to false to keep existing callers
     *   (e.g. AlertContext polling) on the current, one-per-door behavior.
     * @param {string|null} scope - Pass 'today' to have the backend scope the query to
     *   today only (Indian/Mauritius), overriding dateFrom/dateTo - used by the alert
     *   bell dropdown and the polling badge/alarm popup.
     */
    async getAlerts(date = null, page = 1, perPage = 20, vehiclePlate = null, dateFrom = null, dateTo = null, allOccurrences = false, scope = null) {
        try {
            const params = { page, per_page: perPage };
            if (date) params.date = date;
            if (vehiclePlate) params.vehicle_plate = vehiclePlate;
            if (dateFrom) params.date_from = dateFrom;
            if (dateTo) params.date_to = dateTo;
            if (allOccurrences) params.all_occurrences = 1;
            if (scope) params.scope = scope;
            const response = await axiosInstance.get('/alerts', { params });
            return { data: response, error: null };
        } catch (error) {
            return { data: null, error: error.message };
        }
    },

    /**
     * Acknowledge an alert
     * @param {number} alertId - Alert ID
     * @param {string} notes - Reason for acknowledging
     */
    async acknowledgeAlert(alertId, notes = '') {
        try {
            const response = await axiosInstance.post(`/alerts/${alertId}/acknowledge`, {
                notes
            });
            return { data: response, error: null };
        } catch (error) {
            return { data: null, error: error.message };
        }
    },

    /**
     * Resolve an alert
     * @param {number} alertId - Alert ID
     * @param {string} notes - Resolution notes
     */
    async resolveAlert(alertId, notes = '') {
        try {
            const response = await axiosInstance.post(`/alerts/${alertId}/resolve`, {
                notes
            });
            return { data: response, error: null };
        } catch (error) {
            return { data: null, error: error.message };
        }
    },

    /**
     * Get alert settings
     */
    async getAlertSettings() {
        try {
            const response = await axiosInstance.get('/alert-settings');
            return { data: response, error: null };
        } catch (error) {
            return { data: null, error: error.message };
        }
    },

    /**
     * Update alert settings
     * @param {Object} settings - Settings to update
     */
    async updateAlertSettings(settings) {
        try {
            const response = await axiosInstance.put('/alert-settings', settings);
            return { data: response, error: null };
        } catch (error) {
            return { data: null, error: error.message };
        }
    },

    /**
     * Get acknowledgement history
     * @param {string} date - Date in YYYY-MM-DD format (optional)
     * @param {number} page - Page number (default 1)
     * @param {number} perPage - Items per page (default 15)
     */
    async getAcknowledgementHistory(date = null, page = 1, perPage = 15, vehiclePlate = null) {
        try {
            const params = { page, per_page: perPage };
            if (date) params.date = date;
            if (vehiclePlate) params.vehicle_plate = vehiclePlate;
            const response = await axiosInstance.get('/alerts/history', { params });
            return { data: response, error: null };
        } catch (error) {
            return { data: null, error: error.message };
        }
    },

    /**
     * Get alert interval settings by severity (critical / warning), in minutes
     */
    async getSeverityIntervals() {
        try {
            const response = await axiosInstance.get('/alert-settings/severity-intervals');
            return { data: response, error: null };
        } catch (error) {
            return { data: null, error: error.message };
        }
    },

    /**
     * Update alert interval settings by severity
     * @param {Object} data - { critical: number, warning: number }
     */
    async updateSeverityIntervals(data) {
        try {
            const response = await axiosInstance.put('/alert-settings/severity-intervals', data);
            return { data: response, error: null };
        } catch (error) {
            return { data: null, error: error.message };
        }
    },

    /**
     * Get the current user's personal alert settings (sound/push, global override)
     */
    async getUserAlertSettings() {
        try {
            const response = await axiosInstance.get('/alert-settings/me');
            return { data: response, error: null };
        } catch (error) {
            return { data: null, error: error.message };
        }
    },

    /**
     * Update the current user's personal alert settings
     * @param {Object} data - {
     *   use_global: boolean,
     *   sound_enabled: boolean,
     *   push_enabled: boolean,
     *   active_hours_start: string|null ("HH:mm:ss", null = inherit global),
     *   active_hours_end: string|null ("HH:mm:ss", null = inherit global),
     *   alert_interval_minutes: number|null (null = inherit global),
     * }
     */
    async updateUserAlertSettings(data) {
        try {
            const response = await axiosInstance.put('/alert-settings/me', data);
            return { data: response, error: null };
        } catch (error) {
            return { data: null, error: error.message };
        }
    },

    /**
     * Get the alert report (preview) for a date range, optionally scoped to an
     * hour-of-day and/or a set of vehicles
     * @param {Object} params
     * @param {string} params.dateFrom - Range start date (YYYY-MM-DD, required)
     * @param {string} params.dateTo - Range end date (YYYY-MM-DD, required)
     * @param {number|null} params.hourFrom - Hour range start (0-23), optional
     * @param {number|null} params.hourTo - Hour range end (0-23), optional
     * @param {number[]|null} params.vehicleIds - Vehicle ID filter, optional
     * @param {number[]|null} params.categoryIds - Category ID filter, optional
     */
    async getAlertReport({ dateFrom, dateTo, hourFrom = null, hourTo = null, vehicleIds = null, categoryIds = null }) {
        try {
            const params = { date_from: dateFrom, date_to: dateTo };
            if (hourFrom !== null && hourFrom !== undefined && hourFrom !== '') params.hour_from = hourFrom;
            if (hourTo !== null && hourTo !== undefined && hourTo !== '') params.hour_to = hourTo;
            if (vehicleIds && vehicleIds.length > 0) params.vehicle_ids = vehicleIds;
            if (categoryIds && categoryIds.length > 0) params.category_ids = categoryIds;
            const response = await axiosInstance.get('/alerts/report', { params });
            return { data: response, error: null };
        } catch (error) {
            return { data: null, error: error.message };
        }
    },

    /**
     * Export the alert report as a downloadable file (CSV / Excel / PDF)
     * @param {Object} params
     * @param {string} params.dateFrom - Range start date (YYYY-MM-DD, required)
     * @param {string} params.dateTo - Range end date (YYYY-MM-DD, required)
     * @param {number|null} params.hourFrom - Hour range start (0-23), optional
     * @param {number|null} params.hourTo - Hour range end (0-23), optional
     * @param {number[]|null} params.vehicleIds - Vehicle ID filter, optional
     * @param {number[]|null} params.categoryIds - Category ID filter, optional
     * @param {string} params.format - Export format: 'csv' | 'xlsx' | 'pdf'
     */
    async exportAlertReport({ dateFrom, dateTo, hourFrom = null, hourTo = null, vehicleIds = null, categoryIds = null, format }) {
        try {
            const params = { date_from: dateFrom, date_to: dateTo, format };
            if (hourFrom !== null && hourFrom !== undefined && hourFrom !== '') params.hour_from = hourFrom;
            if (hourTo !== null && hourTo !== undefined && hourTo !== '') params.hour_to = hourTo;
            if (vehicleIds && vehicleIds.length > 0) params.vehicle_ids = vehicleIds;
            if (categoryIds && categoryIds.length > 0) params.category_ids = categoryIds;
            const response = await axiosInstance.get('/alerts/report/export', { params, responseType: 'blob' });
            return { data: response, error: null };
        } catch (error) {
            const blob = error.response?.data;
            if (blob instanceof Blob && blob.type.includes('json')) {
                try {
                    const parsed = JSON.parse(await blob.text());
                    return { data: null, error: parsed.message || error.message };
                } catch {
                    return { data: null, error: error.message };
                }
            }
            return { data: null, error: error.message };
        }
    },

    /**
     * Get the full vehicle list (used to populate the report's vehicle filter)
     */
    async getVehicles() {
        try {
            const response = await axiosInstance.get('/vehicles');
            return { data: response, error: null };
        } catch (error) {
            return { data: null, error: error.message };
        }
    }
};
